import { calculateDistance, toLatLngLiteral } from "../../utils/calculateDistance";

export const EMPTY_ROUTE_METRICS = {
  activeRoutes: 0,
  totalRoutes: 0,
  tripsStarted: 0,
  tripsPlanned: 0,
  delayedTrips: 0,
  avgDelayMinutes: 0,
  adherencePercent: null,
  onTrackRoutes: 0,
  offTrackRoutes: 0,
  totalRuntime: 0,
};

const getFirstValue = (row, keys, fallback = "") => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
};

export const flattenStops = (stops) => {
  if (!Array.isArray(stops)) return [];
  return stops.flat(Infinity).filter(Boolean);
};

const isStopRecord = (stop) =>
  Boolean(
    stop &&
      typeof stop === "object" &&
      (stop.id || stop.stopId || stop.name),
  );

export const getRouteStops = (routeOrStops) => {
  if (Array.isArray(routeOrStops)) {
    return flattenStops(routeOrStops).filter(isStopRecord);
  }

  const assignedStops = flattenStops(routeOrStops?.assignedStops).filter(isStopRecord);
  if (assignedStops.length) return assignedStops;

  const stops = flattenStops(routeOrStops?.stops).filter(isStopRecord);
  if (stops.length) return stops;

  return flattenStops(routeOrStops?.routeStops).filter(isStopRecord);
};

export const getRouteStopCount = (route) => getRouteStops(route).length;

export const getStopCorridor = (routeOrStops) => {
  const flattened = getRouteStops(routeOrStops);
  if (!flattened.length) return "No stops";
  if (flattened.length === 1) return flattened[0].name || "Unnamed stop";
  const firstStop = flattened[0].name || "Start";
  const lastStop = flattened[flattened.length - 1].name || "End";
  return `${firstStop} → ${lastStop}`;
};

export const getRouteEndpointStops = (routeOrStops) => {
  const flattened = getRouteStops(routeOrStops);
  if (!flattened.length) {
    return { pickUp: "N/A", drop: "N/A" };
  }
  return {
    pickUp: flattened[0]?.name || "N/A",
    drop:
      flattened.length > 1
        ? flattened[flattened.length - 1]?.name || "N/A"
        : flattened[0]?.name || "N/A",
  };
};

export const getVehicleNames = (vehicles) => {
  if (!vehicles || vehicles.length === 0) return "No vehicles";
  return vehicles.map((vehicle) => vehicle.name || "Unnamed vehicle").join(", ");
};

export const getRouteDistanceKm = (route) => {
  const explicit = Number(
    route?.distanceKm ?? route?.distance ?? route?.totalDistance,
  );
  if (Number.isFinite(explicit) && explicit > 0) return explicit;

  const coordinates = Array.isArray(route?.routeCoordinates)
    ? route.routeCoordinates
    : [];
  if (coordinates.length < 2) return 0;

  let meters = 0;
  for (let index = 1; index < coordinates.length; index += 1) {
    const segment = calculateDistance(coordinates[index - 1], coordinates[index]);
    if (Number.isFinite(segment)) meters += segment;
  }
  return meters / 1000;
};

export const getRouteDurationMinutes = (route, tripRows = []) => {
  const explicit = Number(route?.avgDurationMinutes ?? route?.durationMinutes);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;

  const routeTrips = tripRows.filter(
    (row) => getTripRouteKey(row) === getRouteKey(route),
  );
  const durations = routeTrips
    .map((row) => parseDurationMinutes(row.duration || row.tripDuration || row.totalDuration))
    .filter((value) => Number.isFinite(value) && value > 0);
  if (durations.length) {
    return durations.reduce((sum, value) => sum + value, 0) / durations.length;
  }

  const schedules = Array.isArray(route?.schedules) ? route.schedules : [];
  const scheduleMinutes = schedules
    .map((schedule) => {
      const start = new Date(schedule.startTime);
      const end = new Date(schedule.endTime);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
      return (end.getTime() - start.getTime()) / 60000;
    })
    .filter((value) => Number.isFinite(value) && value > 0);
  if (!scheduleMinutes.length) return 0;
  return scheduleMinutes.reduce((sum, value) => sum + value, 0) / scheduleMinutes.length;
};

export const parseDurationMinutes = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const text = String(value).trim().toLowerCase();
  const hours = Number.parseFloat(text.match(/([\d.]+)\s*h/)?.[1] || 0);
  const minutes = Number.parseFloat(text.match(/([\d.]+)\s*m/)?.[1] || 0);
  const seconds = Number.parseFloat(text.match(/([\d.]+)\s*s/)?.[1] || 0);
  if (hours || minutes || seconds) return hours * 60 + minutes + seconds / 60;

  const numeric = Number.parseFloat(text);
  return Number.isFinite(numeric) ? numeric : null;
};

export const getRouteKey = (route) =>
  String(route?.id ?? route?.routeId ?? route?.name ?? "").trim();

export const getTripRouteKey = (row) =>
  String(
    getFirstValue(row, ["routeId", "routeID", "routeKey", "routeName", "route"], ""),
  ).trim();

export const getTripRouteName = (row) =>
  getFirstValue(row, ["routeName", "route", "routeId"], "Unknown Route");

export const routeMatchesTrip = (route, row) => {
  const rowKey = getTripRouteKey(row);
  if (!rowKey) return false;
  return (
    rowKey === getRouteKey(route) ||
    rowKey === String(route?.name || "") ||
    rowKey === String(route?.routeId || "") ||
    rowKey === String(route?.id || "")
  );
};

const routeHasAssignedVehicles = (route) =>
  Array.isArray(route?.vehicles) && route.vehicles.length > 0;

const routeHasStartedTrips = (route, tripRows = []) =>
  tripRows.some((row) => routeMatchesTrip(route, row) && getReportedTripCount(row) > 0);

export const getReportedTripCount = (row) => {
  if (row?.noOfTrips !== undefined && row?.noOfTrips !== null && row?.noOfTrips !== "") {
    const count = Number(row.noOfTrips);
    return Number.isFinite(count) && count > 0 ? count : 0;
  }
  return 1;
};

export const isDelayedTrip = (row) => {
  const status = String(row?.status || "").toLowerCase();
  return status.includes("delay") || status.includes("late") || status.includes("over");
};

export const isOnTimeTrip = (row) => {
  const status = String(row?.status || "").trim();
  return (
    status.startsWith("In-Time") ||
    status.startsWith("Early") ||
    status.startsWith("Completed")
  );
};

export const isInProgressTrip = (row) =>
  String(row?.status || "").startsWith("In Progress");

export const getTripDelayMinutes = (row) => {
  const explicit = Number(
    getFirstValue(row, ["delayMinutes", "delayedMinutes", "lateMinutes", "delay"], null),
  );
  if (Number.isFinite(explicit) && explicit > 0) return explicit;

  const status = String(row?.status || "");
  const match = status.match(/(\d+(?:\.\d+)?)\s*(?:min|mins|m)/i);
  if (match) return Number.parseFloat(match[1]);
  return 0;
};

export const computePercentChangeTrend = (current, previous) => {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return null;
  }

  const currentValue = Number(current);
  const previousValue = Number(previous);
  if (!Number.isFinite(currentValue) || !Number.isFinite(previousValue)) return null;
  if (previousValue === 0) {
    if (currentValue === 0) return null;
    return {
      trend: "100%",
      trendDirection: currentValue > 0 ? "up" : "down",
    };
  }

  const change = Math.round(((currentValue - previousValue) / Math.abs(previousValue)) * 100);
  if (change === 0) return null;
  return {
    trend: `${Math.abs(change)}%`,
    trendDirection: change > 0 ? "up" : "down",
  };
};

export const toReportDateOption = (value) =>
  String(value || "today")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");

export const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getPeriodBounds = (dateOption, customRange = {}) => {
  const option = toReportDateOption(dateOption);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  let start = new Date(today);
  let end = new Date(endOfToday);

  if (option === "yesterday") {
    start.setDate(today.getDate() - 1);
    end = new Date(start);
    end.setHours(23, 59, 59, 999);
  } else if (option === "custom") {
    if (customRange.start && customRange.end) {
      start = new Date(`${customRange.start}T00:00:00`);
      end = new Date(`${customRange.end}T23:59:59`);
    }
  } else if (option === "last 7 days") {
    start.setDate(today.getDate() - 6);
  } else if (option === "last 14 days") {
    start.setDate(today.getDate() - 13);
  } else if (option === "last 30 days") {
    start.setDate(today.getDate() - 29);
  } else if (option === "this week") {
    const weekday = today.getDay();
    const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
    start.setDate(today.getDate() + mondayOffset);
  } else if (option === "this month") {
    start.setDate(1);
  }

  return { start, end };
};

export const getComparisonPeriodParams = (dateOption, customRange = {}) => {
  const option = toReportDateOption(dateOption);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (option === "today") return { dateOption: "yesterday" };
  if (option === "yesterday") {
    const day = new Date(today);
    day.setDate(today.getDate() - 2);
    return {
      dateOption: "custom",
      customStartDate: formatDateInput(day),
      customEndDate: formatDateInput(day),
    };
  }

  const { start, end } = getPeriodBounds(option, customRange);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return { dateOption: "yesterday" };
  }

  const durationMs = Math.max(end.getTime() - start.getTime(), 24 * 60 * 60 * 1000 - 1);
  const prevEnd = new Date(start.getTime() - 1);
  const prevStart = new Date(prevEnd.getTime() - durationMs);

  return {
    dateOption: "custom",
    customStartDate: formatDateInput(prevStart),
    customEndDate: formatDateInput(prevEnd),
  };
};

const startOfLocalDay = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getScheduleRepetition = (schedule) =>
  String(schedule?.repitation || schedule?.repetition || "")
    .trim()
    .toUpperCase();

const scheduleOccursOnDay = (schedule, day) => {
  const start = new Date(schedule?.startTime || schedule?.startDate || schedule?.start);
  if (Number.isNaN(start.getTime())) return false;

  const startDay = startOfLocalDay(start);
  const current = startOfLocalDay(day);
  if (current < startDay) return false;

  const endRaw = schedule?.endDate || schedule?.byDateEnd || null;
  if (endRaw) {
    const endDay = startOfLocalDay(endRaw);
    if (!Number.isNaN(endDay.getTime()) && current > endDay) return false;
  }

  const repetition = getScheduleRepetition(schedule);
  const repeatEvery = Math.max(1, Number(schedule?.repeatEvery) || 1);
  const diffDays = Math.round((current.getTime() - startDay.getTime()) / 86400000);

  if (repetition === "ONETIME") return diffDays === 0;
  if (repetition === "WEEKLY") {
    const weeklyDays = Array.isArray(schedule?.weeklyDays) && schedule.weeklyDays.length
      ? schedule.weeklyDays.map((value) => Number(value))
      : [start.getDay()];
    if (!weeklyDays.includes(current.getDay())) return false;
    const weekDiff = Math.floor(diffDays / 7);
    return weekDiff % repeatEvery === 0;
  }
  if (repetition === "MONTHLY") {
    return current.getDate() === start.getDate();
  }
  return diffDays % repeatEvery === 0;
};

export const countScheduledTrips = (routes = [], dateOption, customRange = {}) => {
  const { start, end } = getPeriodBounds(dateOption, customRange);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;

  let count = 0;
  const cursor = startOfLocalDay(start);
  const last = startOfLocalDay(end);
  while (cursor <= last) {
    routes.forEach((route) => {
      (Array.isArray(route?.schedules) ? route.schedules : []).forEach((schedule) => {
        if (scheduleOccursOnDay(schedule, cursor)) count += 1;
      });
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const getRowRuntimeHours = (row) => {
  const numericKeys = [
    "totalRuntimeHours",
    "totalMovingHours",
    "runtimeHours",
    "movingHours",
    "engineHours",
    "durationHours",
  ];
  for (const key of numericKeys) {
    const value = Number(row?.[key]);
    if (Number.isFinite(value) && value > 0) return value;
  }

  const parsedMinutes = parseDurationMinutes(
    getFirstValue(row, [
      "totalRuntime",
      "runtime",
      "duration",
      "totalDuration",
      "movingTime",
      "engineTime",
    ], null),
  );
  return parsedMinutes && parsedMinutes > 0 ? parsedMinutes / 60 : 0;
};

const getAssignedVehicleKeys = (routes = []) => {
  const keys = new Set();
  routes.forEach((route) => {
    (Array.isArray(route?.vehicles) ? route.vehicles : []).forEach((vehicle) => {
      [
        vehicle?.id,
        vehicle?.deviceId,
        vehicle?.vehicleId,
        vehicle?.name,
        vehicle?.vehicleName,
        vehicle?.VehicleName,
        vehicle?.VehicleNumber,
        vehicle?.vehicleNumber,
      ]
        .filter((value) => value !== undefined && value !== null && value !== "")
        .forEach((value) => keys.add(String(value).trim().toLowerCase()));
    });
  });
  return keys;
};

const runtimeRowMatchesRouteVehicles = (row, assignedKeys) => {
  if (!assignedKeys.size) return true;
  return [
    row?.vehicleId,
    row?.deviceId,
    row?.vehicleName,
    row?.deviceName,
    row?.name,
    row?.VehicleNumber,
    row?.vehicleNumber,
  ].some(
    (value) =>
      value !== undefined &&
      value !== null &&
      value !== "" &&
      assignedKeys.has(String(value).trim().toLowerCase()),
  );
};

const sumRuntimeHours = (rows = []) =>
  rows.reduce((total, row) => total + getRowRuntimeHours(row), 0);

const sumTripRuntimeHours = (tripRows = []) =>
  tripRows.reduce((total, row) => {
    const minutes = parseDurationMinutes(
      getFirstValue(row, ["duration", "tripDuration", "totalDuration", "durationHours"], null),
    );
    if (minutes && minutes > 0) return total + minutes / 60;
    const start = new Date(row?.startTime);
    const end = new Date(row?.endTime);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
      return total;
    }
    return total + (end.getTime() - start.getTime()) / 3600000;
  }, 0);

const countBy = (items, getKey) =>
  items.reduce((acc, item) => {
    const key = getKey(item) || "Unknown";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

const toSortedRows = (countMap, limit = 6) =>
  Object.entries(countMap)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);

const getRouteTrackCounts = (routes = [], tripRows = [], deviationRows = []) => {
  let onTrackRoutes = 0;
  let offTrackRoutes = 0;

  routes.forEach((route) => {
    const status = getRouteOperationalStatus(route, tripRows, deviationRows).label;
    if (status === "Idle") return;
    if (status === "On Route") onTrackRoutes += 1;
    else offTrackRoutes += 1;
  });

  const evaluated = onTrackRoutes + offTrackRoutes;
  return {
    onTrackRoutes,
    offTrackRoutes,
    adherencePercent: evaluated ? Math.round((onTrackRoutes / evaluated) * 100) : null,
  };
};

export const buildRouteMetrics = (
  routes = [],
  tripRows = [],
  runtimeRows = [],
  deviationRows = [],
  period = {},
) => {
  const delayedTrips = tripRows.filter(isDelayedTrip);
  const delayMinutes = delayedTrips.map(getTripDelayMinutes).filter((value) => value > 0);
  const activeRoutes = routes.filter(
    (route) => routeHasAssignedVehicles(route) && routeHasStartedTrips(route, tripRows),
  ).length;
  const scheduledTrips = countScheduledTrips(
    routes,
    period.dateOption,
    period.customRange,
  );
  const trackCounts = getRouteTrackCounts(routes, tripRows, deviationRows);
  const assignedVehicleKeys = getAssignedVehicleKeys(routes);
  const matchingRuntimeRows = assignedVehicleKeys.size
    ? runtimeRows.filter((row) => runtimeRowMatchesRouteVehicles(row, assignedVehicleKeys))
    : runtimeRows;
  const runtimeHours =
    sumRuntimeHours(matchingRuntimeRows.length ? matchingRuntimeRows : runtimeRows) ||
    sumTripRuntimeHours(tripRows);

  return {
    activeRoutes,
    totalRoutes: routes.length,
    tripsStarted: tripRows.reduce((sum, row) => sum + getReportedTripCount(row), 0),
    tripsPlanned: scheduledTrips,
    delayedTrips: delayedTrips.length,
    avgDelayMinutes: delayMinutes.length
      ? Math.round(delayMinutes.reduce((sum, value) => sum + value, 0) / delayMinutes.length)
      : 0,
    adherencePercent: trackCounts.adherencePercent,
    onTrackRoutes: trackCounts.onTrackRoutes,
    offTrackRoutes: trackCounts.offTrackRoutes,
    totalRuntime: runtimeHours,
  };
};

export const buildRouteDashboardCharts = ({
  routes = [],
  tripRows = [],
  deviationRows = [],
} = {}) => {
  const delayedByRoute = {};
  tripRows.filter(isDelayedTrip).forEach((row) => {
    const name = getTripRouteName(row);
    const minutes = getTripDelayMinutes(row);
    if (!delayedByRoute[name]) {
      delayedByRoute[name] = { label: name, minutes: 0, count: 0 };
    }
    delayedByRoute[name].minutes += minutes;
    delayedByRoute[name].count += 1;
  });

  const topDelayedRoutes = Object.values(delayedByRoute)
    .map((row) => {
      const route = routes.find(
        (item) => item.name === row.label || String(item.routeId) === row.label,
      );
      const avgMinutes = row.count ? Math.round(row.minutes / row.count) : row.minutes;
      return {
        label: row.label,
        subtitle: getStopCorridor(route),
        minutes: avgMinutes || 0,
        badge: `${avgMinutes || 0} min`,
        value: avgMinutes,
      };
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);

  const vehiclesByRoute = toSortedRows(
    routes.reduce((acc, route) => {
      const name = route.name || route.routeId || "Unknown";
      acc[name] = Array.isArray(route.vehicles) ? route.vehicles.length : 0;
      return acc;
    }, {}),
    6,
  )
    .filter((row) => row.value > 0)
    .map((row, index) => ({
      ...row,
      color: index % 2 === 0 ? "#3b82f6" : "#93c5fd",
    }));

  const routeDeviations = toSortedRows(
    countBy(
      deviationRows.filter((row) => Number(row.outOfRouteCount || row.deviations || 1) > 0),
      (row) => getTripRouteName(row),
    ),
    6,
  );

  const delayByRoute = Object.values(delayedByRoute)
    .map((row) => {
      const minutes = row.count ? Math.round(row.minutes / row.count) : 0;
      return {
        label: row.label,
        value: minutes,
        displayValue: `${minutes}m`,
        color: "#f97316",
      };
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);

  const trackCounts = getRouteTrackCounts(routes, tripRows, deviationRows);

  return {
    topDelayedRoutes,
    vehiclesByRoute,
    routeDeviations,
    delayByRoute,
    adherencePercent: trackCounts.adherencePercent,
    adherenceRows: [
      { label: "On Track", value: trackCounts.onTrackRoutes, color: "#22c55e" },
      { label: "Off Track", value: trackCounts.offTrackRoutes, color: "#ef4444" },
    ],
  };
};

export const getRouteOperationalStatus = (route, tripRows = [], deviationRows = []) => {
  const matches = (row) => routeMatchesTrip(route, row);
  const routeTrips = tripRows.filter(matches);

  if (deviationRows.some(matches)) {
    return { label: "Deviated", tone: "red" };
  }
  if (routeTrips.some(isDelayedTrip)) {
    return { label: "Delayed", tone: "amber" };
  }
  if (routeHasAssignedVehicles(route) && routeTrips.length > 0) {
    return { label: "On Route", tone: "green" };
  }
  return { label: "Idle", tone: "gray" };
};

export const getRouteAdherencePercent = (route, tripRows = [], deviationRows = []) => {
  const status = getRouteOperationalStatus(route, tripRows, deviationRows).label;
  if (status === "Idle") return null;

  const routeTrips = tripRows.filter((row) => routeMatchesTrip(route, row));
  const finished = routeTrips.filter((row) => {
    const tripStatus = String(row?.status || "").trim().toLowerCase();
    if (!tripStatus || tripStatus === "n/a" || tripStatus.startsWith("in progress")) return false;
    if (tripStatus.startsWith("not completed")) return false;
    return true;
  });
  if (finished.length) {
    return Math.round((finished.filter(isOnTimeTrip).length / finished.length) * 100);
  }
  return null;
};

export const formatDurationLabel = (minutes, translate) => {
  if (!Number.isFinite(minutes) || minutes <= 0) return "--";
  const rounded = Math.round(minutes);
  const unit = translate ? translate("mins") : "mins";
  return `${rounded} ${unit}`;
};

export const formatDistanceLabel = (km) => {
  if (!Number.isFinite(km) || km <= 0) return "--";
  return `${km.toFixed(1)} km`;
};

const toTimestamp = (value) => {
  if (value === undefined || value === null || value === "") return 0;
  if (typeof value === "number") return value < 1e12 ? value * 1000 : value;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

export const getRouteLastUpdateValue = (route, tripRows = []) => {
  const candidates = [
    route?.updatedAt,
    route?.updated_at,
    route?.lastUpdate,
    route?.lastUpdated,
  ];

  (Array.isArray(route?.vehicles) ? route.vehicles : []).forEach((vehicle) => {
    candidates.push(
      vehicle?.lastUpdate,
      vehicle?.lastUpdated,
      vehicle?.updatedAt,
      vehicle?.timestamp,
      vehicle?.latestLocation?.timestamp,
      vehicle?.lastLocation?.timestamp,
    );
  });

  tripRows.filter((row) => routeMatchesTrip(route, row)).forEach((row) => {
    candidates.push(row.endTime, row.startTime, row.timestamp, row.lastUpdate);
  });

  return candidates.reduce((latest, value) => {
    const time = toTimestamp(value);
    return time > latest ? time : latest;
  }, 0) || null;
};

const getCoordinateKey = (pointOrLat, longitude) => {
  const coords =
    pointOrLat != null && typeof pointOrLat === "object"
      ? toLatLngLiteral(pointOrLat)
      : toLatLngLiteral({ latitude: pointOrLat, longitude });
  if (!coords) return "";
  return `${coords.lat.toFixed(6)}_${coords.lng.toFixed(6)}`;
};

export const buildRouteFullData = (route, allOrgStops = []) => {
  const stopsById = new Map(
    allOrgStops.filter((stop) => stop?.id).map((stop) => [stop.id, stop]),
  );
  const stopsByCoordinate = new Map(
    allOrgStops
      .map((stop) => [getCoordinateKey(stop), stop])
      .filter(([key]) => key),
  );

  const routeCoordinates = Array.isArray(route.routeCoordinates)
    ? [...route.routeCoordinates].sort(
        (a, b) => Number(a?.order || 0) - Number(b?.order || 0),
      )
    : [];

  if (routeCoordinates.length === 0) {
    return {
      ...route,
      stops: getRouteStops(route),
      origin: null,
      destination: null,
    };
  }

  const allPoints = routeCoordinates
    .map((coordinate, index) => {
      const coords = toLatLngLiteral(coordinate);
      if (!coords) return null;
      const matchedStop =
        (coordinate.stopId ? stopsById.get(coordinate.stopId) : null) ||
        stopsByCoordinate.get(getCoordinateKey(coords.lat, coords.lng)) ||
        null;
      const isStop = Boolean(
        matchedStop || coordinate.isStop || coordinate.stopId,
      );
      const defaultName =
        index === 0
          ? "Origin"
          : index === routeCoordinates.length - 1
            ? "Destination"
            : isStop
              ? `Stop ${index}`
              : `IM${index}`;

      return {
        lat: coords.lat,
        lng: coords.lng,
        isStop,
        stopId: matchedStop?.id || coordinate.stopId || null,
        name: matchedStop?.name || coordinate.name || defaultName,
        id:
          matchedStop?.id ||
          coordinate.stopId ||
          coordinate.id ||
          `route-point-${route.id || route.routeId}-${index}`,
      };
    })
    .filter(Boolean);

  const originPoint = allPoints[0];
  const destinationPoint = allPoints[allPoints.length - 1];
  const midPoints = allPoints.length > 2 ? allPoints.slice(1, -1) : [];
  const orderedStops = [
    originPoint,
    ...midPoints.filter((point) => point.isStop),
    destinationPoint?.id !== originPoint?.id ? destinationPoint : null,
  ]
    .filter(Boolean)
    .map((point) => ({
      id: point.stopId,
      latitude: point.lat,
      longitude: point.lng,
      name: point.name,
    }));

  return {
    ...route,
    stops: orderedStops.length ? orderedStops : getRouteStops(route),
    origin: originPoint
      ? {
          id: originPoint.stopId,
          latitude: originPoint.lat,
          longitude: originPoint.lng,
          name: originPoint.name,
        }
      : null,
    destination: destinationPoint
      ? {
          id: destinationPoint.stopId,
          latitude: destinationPoint.lat,
          longitude: destinationPoint.lng,
          name: destinationPoint.name,
        }
      : null,
  };
};

